import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Email as EmailIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

// API dedicada para requisições de autenticação (sem token)
const authApi = axios.create({
  baseURL: `${process.env.REACT_APP_SCALAR_API_URL}/auth`,
  timeout: 10000,
});

export function ForgotPasswordModal({ open, onClose }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (loading) return; // Previne múltiplas submissões

    setError('');
    setLoading(true);

    try {
      const response = await authApi.post('/forgot-password', { 
        email,
        originUrl: window.location.origin 
      });

      setSuccess(true);
      setEmail(''); // Limpa o campo

    } catch (error: any) {
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.code === 'ECONNABORTED') {
        setError('Timeout na requisição. Tente novamente.');
      } else if (error.code === 'NETWORK_ERROR') {
        setError('Erro de conexão. Verifique sua internet.');
      } else {
        setError('Erro interno. Tente novamente em alguns instantes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && email.trim()) {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6" component="span">
            Recuperar Senha
          </Typography>
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon 
              sx={{ fontSize: 48, color: 'success.main', mb: 2 }} 
            />
            <Typography variant="h6" color="success.main" gutterBottom>
              Email Enviado!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Digite seu email para receber um link de recuperação de senha.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              required
              sx={{ mb: 2 }}
              placeholder="seu@email.com"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {success ? (
          <Button 
            onClick={handleClose} 
            variant="contained" 
            fullWidth
            size="large"
          >
            Fechar
          </Button>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              color="inherit"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Enviando...' : 'Enviar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}